import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';

interface Decision {
  id: string;
  title: string;
  description: string;
  status: 'proposed' | 'approved' | 'implemented' | 'rejected' | 'deferred';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  proposed_by: string;
  approved_by: string | null;
  implemented_by: string | null;
  rejected_by: string | null;
  rejection_reason: string | null;
  due_date: string | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
}

const DecisionsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showDecisionForm, setShowDecisionForm] = useState(false);
  const [editingDecision, setEditingDecision] = useState<Decision | null>(null);
  const [newDecision, setNewDecision] = useState<{
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
    category: string;
    due_date: string;
  }>({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    due_date: '',
  });

  const queryClient = useQueryClient();

  const {
    data: decisions,
    isLoading,
    error,
  } = useQuery<Decision[]>({
    queryKey: ['tracker-decisions', searchTerm, selectedStatus, selectedPriority, selectedCategory],
    queryFn: async (): Promise<Decision[]> => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedPriority) params.append('priority', selectedPriority);
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await apiService.get(`/tracker/decisions?${params.toString()}`);
      return (response as Decision[]) || [];
    },
    refetchInterval: 30000,
  });

  const createDecisionMutation = useMutation({
    mutationFn: async (decisionData: typeof newDecision) => {
      return await apiService.post('/tracker/decisions', decisionData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-decisions'] });
      setShowDecisionForm(false);
      resetForm();
    },
  });

  const updateDecisionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Decision> }) => {
      return await apiService.put(`/tracker/decisions/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-decisions'] });
      setEditingDecision(null);
      resetForm();
    },
  });

  const deleteDecisionMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiService.delete(`/tracker/decisions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-decisions'] });
    },
  });

  const resetForm = () => {
    setNewDecision({
      title: '',
      description: '',
      priority: 'medium',
      category: '',
      due_date: '',
    });
  };

  const handleCreateDecision = () => {
    if (newDecision.title.trim() && newDecision.description.trim()) {
      createDecisionMutation.mutate(newDecision);
    }
  };

  const handleUpdateDecision = (id: string, updates: Partial<Decision>) => {
    updateDecisionMutation.mutate({ id, data: updates });
  };

  const handleDeleteDecision = (id: string) => {
    if (window.confirm('Are you sure you want to delete this decision?')) {
      deleteDecisionMutation.mutate(id);
    }
  };

  const handleEditDecision = (decision: Decision) => {
    setEditingDecision(decision);
    setNewDecision({
      title: decision.title,
      description: decision.description,
      priority: decision.priority,
      category: decision.category,
      due_date: decision.due_date || '',
    });
    setShowDecisionForm(true);
  };

  const handleSaveEdit = () => {
    if (editingDecision && newDecision.title.trim() && newDecision.description.trim()) {
      updateDecisionMutation.mutate({
        id: editingDecision.id,
        data: newDecision,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'proposed':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'implemented':
        return 'bg-purple-100 text-purple-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'deferred':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'high':
        return 'text-orange-600 bg-orange-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredDecisions = decisions || [];
  const categories = Array.from(
    new Set(filteredDecisions.map((decision: Decision) => decision.category))
  );
  const statuses = ['proposed', 'approved', 'implemented', 'rejected', 'deferred'];
  const priorities = ['low', 'medium', 'high', 'critical'];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading decisions</h3>
              <p className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Decision Log</h1>
          <p className="text-gray-600">Track and manage project decisions</p>
        </div>
        <button
          onClick={() => setShowDecisionForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Decision
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search decisions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
        </div>
        <div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status} className="capitalize">
                {status}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Priorities</option>
            {priorities.map((priority) => (
              <option key={priority} value={priority} className="capitalize">
                {priority}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((category: string) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-gray-600 flex items-center">
          Total: {filteredDecisions.length} decisions
        </div>
      </div>

      {/* Decisions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDecisions.map((decision: Decision) => (
          <div
            key={decision.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">{decision.title}</h3>
                <div className="flex items-center space-x-2 mt-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(decision.status)}`}
                  >
                    {decision.status}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getPriorityColor(decision.priority)}`}
                  >
                    {decision.priority}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-1 ml-2">
                <button
                  onClick={() => handleEditDecision(decision)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="Edit Decision"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteDecision(decision.id)}
                  className="text-gray-400 hover:text-red-600 p-1"
                  title="Delete Decision"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4 line-clamp-3">{decision.description}</p>

            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex items-center">
                <UserIcon className="h-3 w-3 mr-1" />
                Proposed by: {decision.proposed_by}
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-3 w-3 mr-1" />
                Due: {formatDate(decision.due_date)}
              </div>
              {decision.category && (
                <div className="flex items-center">
                  <InformationCircleIcon className="h-3 w-3 mr-1" />
                  Category: {decision.category}
                </div>
              )}
            </div>

            {/* Status-specific actions */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              {decision.status === 'proposed' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleUpdateDecision(decision.id, { status: 'approved' })}
                    className="flex-1 inline-flex justify-center items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleUpdateDecision(decision.id, { status: 'rejected' })}
                    className="flex-1 inline-flex justify-center items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Reject
                  </button>
                </div>
              )}
              {decision.status === 'approved' && (
                <button
                  onClick={() => handleUpdateDecision(decision.id, { status: 'implemented' })}
                  className="w-full inline-flex justify-center items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-purple-600 hover:bg-purple-700"
                >
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Mark Implemented
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredDecisions.length === 0 && (
        <div className="text-center py-12">
          <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No decisions found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedStatus || selectedPriority || selectedCategory
              ? 'Try adjusting your filters.'
              : 'Start by adding your first decision!'}
          </p>
        </div>
      )}

      {/* Add/Edit Decision Modal */}
      {showDecisionForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingDecision ? 'Edit Decision' : 'Add New Decision'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={newDecision.title}
                    onChange={(e) => setNewDecision({ ...newDecision, title: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter decision title..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newDecision.description}
                    onChange={(e) =>
                      setNewDecision({ ...newDecision, description: e.target.value })
                    }
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter decision description..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority</label>
                    <select
                      value={newDecision.priority}
                      onChange={(e) =>
                        setNewDecision({
                          ...newDecision,
                          priority: e.target.value as typeof newDecision.priority,
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <input
                      type="text"
                      value={newDecision.category}
                      onChange={(e) => setNewDecision({ ...newDecision, category: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Technical, Business..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Due Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={newDecision.due_date}
                    onChange={(e) => setNewDecision({ ...newDecision, due_date: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDecisionForm(false);
                    setEditingDecision(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={editingDecision ? handleSaveEdit : handleCreateDecision}
                  disabled={
                    !newDecision.title.trim() ||
                    !newDecision.description.trim() ||
                    (editingDecision
                      ? updateDecisionMutation.isLoading
                      : createDecisionMutation.isLoading)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingDecision
                    ? updateDecisionMutation.isLoading
                      ? 'Saving...'
                      : 'Save Changes'
                    : createDecisionMutation.isLoading
                      ? 'Creating...'
                      : 'Create Decision'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DecisionsPage;
